import { LAYOUT_NAMESPACE } from '@/modules/Layout/stores';
import { endOfDay, endOfMonth, endOfYear, format, startOfDay, startOfMonth, startOfYear } from 'date-fns';

export const ANALYSIS_NAMESPACE = 'ANALYSIS_NAMESPACE';

// Share of a total in percent, clamped to 0 - 100. Generation and consumption no longer
// contain the battery, while feed in and grid supply still come from the balance including
// it. A battery emptying into the grid can therefore exceed the generation of the same day,
// which without the clamp would show as a negative self use.
const share = (part, total) => {
  if (0 === total) {
    return 0;
  }

  return Math.min(100, Math.max(0, (100 / total) * part));
};

// The remainder of that share. Without a total there is nothing to show, so it stays at 0
// instead of becoming a full 100 percent.
const remainingShare = (part, total) => {
  if (0 === total) {
    return 0;
  }

  return 100 - share(part, total);
};

export const configureAnalysisStore = (Vue) => {
  return {
    namespaced: true,
    state: {
      analysisData: {
        totalGeneration: 0.0,
        totalConsumption: 0.0,
        totalSaved: 0.0,
        moneySaved: 0.0,
        co2Saved: 0.0,
      },
      generationAndConsumptionData: {
        scalingFormData: {
          scaling: 'day',
          date: new Date(),
        },
        timespanData: {
          combined: [],
          generator: [],
          consumer: [],
        },
        pieData: {
          totalGeneration: 0.0,
          totalConsumption: 0.0,
          generatedUsed: 0.0,
          generatedUnused: 0.0,
          consumedOwned: 0.0,
          consumedPayed: 0.0,
        },
      },
      profitability: {
        scalingFormData: {
          scaling: 'day',
          date: new Date(),
        },
        energyBalance: {
          totalGeneration: 0.0,
          totalConsumption: 0.0,
          totalSaved: 0.0,
          moneySaved: 0.0,
          co2Saved: 0.0,
          powerSupply: 0,
        },
      },
      economyEfficiencyConversionFactors: [
        {
          identifier: '',
          description: '',
          unitOfMeasure: '',
          heatingValuePerUnit: '',
          conversionFactor: '',
          pricePerUnit: '',
        },
      ],
      ecology: {
        totalGeneration: 0.0,
        totalConsumption: 0.0,
        totalSaved: 0.0,
        moneySaved: 0.0,
        co2Saved: 0.0,
        powerSupply: 0.0,
        selfSufficiency: 0,
        feed: 0,
      },
      selfSufficiencyConversionFactors: [
        {
          identifier: '',
          description: '',
          conversionFactor: '',
        },
      ],
    },
    mutations: {
      setAnalysisData(state, payload) {
        state.analysisData = payload;
      },
      setGenerationAndConsumptionData(state, payload) {
        state.generationAndConsumptionData = payload;
      },
      setProfitability(state, payload) {
        state.profitability = payload;
      },
      setEconomyEfficiencyConversionFactors(state, payload) {
        state.economyEfficiencyConversionFactors = payload;
      },
      setEcology(state, payload) {
        state.ecology = payload;
      },
      setSelfSufficiencyConversionFactors(state, payload) {
        state.selfSufficiencyConversionFactors = payload;
      },
    },
    actions: {
      async fetchAnalysisData({ rootState, commit }) {
        const sunbrainId = rootState[LAYOUT_NAMESPACE].currentDeviceId;
        const response = await Vue.prototype.$http.get('/customer-ui/dashboard/analysis-data', {
          params: {
            sunbrainId,
          },
        });
        if (sunbrainId === rootState[LAYOUT_NAMESPACE].currentDeviceId) {
          commit('setAnalysisData', {
            totalGeneration: response.data.totalGeneration ?? 0.0,
            totalConsumption: response.data.totalConsumption ?? 0.0,
            totalSaved: response.data.totalSaved ?? 0.0,
            moneySaved: response.data.moneySaved ?? 0.0,
            co2Saved: response.data.co2Saved ?? 0.0,
          });
        }
      },
      async fetchGenerationAndConsumptionData({ rootState, state, commit }) {
        const sunbrainId = rootState[LAYOUT_NAMESPACE].currentDeviceId;
        const { date, scaling } = state.generationAndConsumptionData.scalingFormData;
        const timespanConfig = {};
        const pieConfig = {};
        switch (scaling) {
          case 'year':
            timespanConfig.params = {
              sunbrainId,
              startDate: format(startOfYear(date), 'yyyy-MM-dd'),
              endDate: format(endOfYear(date), 'yyyy-MM-dd'),
              resolution: 'month',
            };
            pieConfig.params = {
              sunbrainId,
              time: format(date, 'yyyy'),
              resolution: 'year',
            };
            break;
          case 'month':
            timespanConfig.params = {
              sunbrainId,
              startDate: format(startOfMonth(date), 'yyyy-MM-dd'),
              endDate: format(endOfMonth(date), 'yyyy-MM-dd'),
              resolution: 'day',
            };
            pieConfig.params = {
              sunbrainId,
              time: format(date, 'MM-yyyy'),
              resolution: 'month',
            };
            break;
          default:
            timespanConfig.params = {
              sunbrainId,
              startDate: format(startOfDay(date), 'yyyy-MM-dd'),
              endDate: format(endOfDay(date), 'yyyy-MM-dd'),
              resolution: 'minute',
            };
            pieConfig.params = {
              sunbrainId,
              time: format(date, 'yyyy-MM-dd'),
              resolution: 'day',
            };
        }
        const timespanResponse = await Vue.prototype.$http.get('/customer-ui/analysis/timespan-data', timespanConfig);
        const pieResponse = await Vue.prototype.$http.get('/customer-ui/analysis/energy-balance', pieConfig);
        if (
          sunbrainId === rootState[LAYOUT_NAMESPACE].currentDeviceId &&
          date === state.generationAndConsumptionData.scalingFormData.date &&
          scaling === state.generationAndConsumptionData.scalingFormData.scaling
        ) {
          const totalGeneration = parseFloat(pieResponse.data.totalGeneration ?? 0.0);
          const totalConsumption = parseFloat(pieResponse.data.totalConsumption ?? 0.0);
          const totalSupply = parseFloat(pieResponse.data.totalSupply ?? 0.0);
          const totalFeedIn = parseFloat(pieResponse.data.totalFeedIn ?? 0.0);
          commit('setGenerationAndConsumptionData', {
            ...state.generationAndConsumptionData,
            timespanData: {
              combined: timespanResponse.data.combined || [],
              generator: timespanResponse.data.generator || [],
              consumer: timespanResponse.data.consumer || [],
            },
            pieData: {
              totalGeneration,
              totalConsumption,
              totalSupply,
              totalFeedIn,
              generatedUsed: remainingShare(totalFeedIn, totalGeneration),
              generatedUnused: share(totalFeedIn, totalGeneration),
              consumedOwned: remainingShare(totalSupply, totalConsumption),
              consumedPayed: share(totalSupply, totalConsumption),
            },
          });
        }
      },
      async fetchEnergyBalance({ rootState, state, commit }) {
        const sunbrainId = rootState[LAYOUT_NAMESPACE].currentDeviceId;
        const { date, scaling } = state.profitability.scalingFormData;
        const config = {};
        switch (scaling) {
          case 'year':
            config.params = {
              sunbrainId,
              time: format(date, 'yyyy'),
              resolution: 'year',
            };
            break;
          case 'month':
            config.params = {
              sunbrainId,
              time: format(date, 'MM-yyyy'),
              resolution: 'month',
            };
            break;
          default:
            config.params = {
              sunbrainId,
              time: format(date, 'yyyy-MM-dd'),
              resolution: 'day',
            };
        }
        const response = await Vue.prototype.$http.get('/customer-ui/analysis/energy-balance', config);
        if (
          sunbrainId === rootState[LAYOUT_NAMESPACE].currentDeviceId &&
          date === state.profitability.scalingFormData.date &&
          scaling === state.profitability.scalingFormData.scaling
        ) {
          commit('setProfitability', {
            ...state.profitability,
            energyBalance: {
              totalGeneration: response.data.totalGeneration ?? 0.0,
              totalConsumption: response.data.totalConsumption ?? 0.0,
              totalSaved: response.data.totalSaved ?? 0.0,
              moneySaved: response.data.moneySaved ?? 0.0,
              co2Saved: response.data.co2Saved ?? 0.0,
              powerSupply: response.data.powerSupply ?? 0,
            },
          });
        }
      },
      async fetchEconomyEfficiencyConversionFactors({ commit }) {
        const response = await Vue.prototype.$http.get('/customer-ui/conversion-factors/economic-efficiency/list');
        commit(
          'setEconomyEfficiencyConversionFactors',
          response.data.map((factor) => ({
            identifier: factor.identifier,
            description: factor.description,
            unitOfMeasure: factor.unitOfMeasure,
            heatingValuePerUnit: factor.heatingValuePerUnit,
            conversionFactor: factor.conversionFactor,
            pricePerUnit: factor.pricePerUnit,
          }))
        );
      },
      async fetchEcology({ rootState, commit }) {
        const sunbrainId = rootState[LAYOUT_NAMESPACE].currentDeviceId;
        const config = {
          params: {
            sunbrainId,
            time: format(new Date(), 'yyyy'),
            resolution: 'year',
          },
        };
        const response = await Vue.prototype.$http.get('/customer-ui/analysis/energy-balance', config);
        if (sunbrainId === rootState[LAYOUT_NAMESPACE].currentDeviceId) {
          const totalGeneration = parseFloat(response.data.totalGeneration ?? 0.0);
          const totalConsumption = parseFloat(response.data.totalConsumption ?? 0.0);
          const totalSupply = parseFloat(response.data.totalSupply ?? 0.0);
          const totalFeedIn = parseFloat(response.data.totalFeedIn ?? 0.0);
          commit('setEcology', {
            totalGeneration,
            totalConsumption,
            totalSupply,
            totalFeedIn,
            generatedUsed: remainingShare(totalFeedIn, totalGeneration),
            generatedUnused: share(totalFeedIn, totalGeneration),
            consumedOwned: remainingShare(totalSupply, totalConsumption),
            consumedPayed: share(totalSupply, totalConsumption),
            totalSaved: response.data.totalSaved ?? 0.0,
            moneySaved: response.data.moneySaved ?? 0.0,
            co2Saved: response.data.co2Saved ?? 0.0,
          });
        }
      },
      async fetchSelfSufficiencyConversionFactors({ commit }) {
        const response = await Vue.prototype.$http.get('/customer-ui/conversion-factors/self-sufficiency/list');
        commit(
          'setSelfSufficiencyConversionFactors',
          response.data.map((factor) => ({
            identifier: factor.identifier,
            description: factor.description,
            conversionFactor: factor.conversionFactor,
          }))
        );
      },
    },
    getters: {},
  };
};
